import { CSSProperties } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { patchRoutine, useRoutines, type WebRoutine } from '../../../lib/routines';
import { routineIsOn, routineToggleEnabled } from '../../../lib/routine-status';
import { useRoutineCopy } from '../routines/use-routine-copy';
import { RoutineDetailModal } from '../routines/RoutineDetailModal';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { Button, Card, Switch } from '../ui';
import styles from './Home2RoutinesCard.module.css';

// Home 2 (symphonyHome2Web) — the Routines bento card (Figma 1859:2783), the right slot of Row 1,
// single-workspace layout. A preview of the user's routines, each with its schedule and a working
// on/off toggle, over an enabled-count summary and an "Open Routines" action.
//
// NOTE: the Figma also shows "Recommended" routines (suggested automations the user hasn't enabled)
// with a count in the footer. There is NO data source for those in orion-web today — every routine the
// app knows about is one the user already has — so per real-data-only they are deferred: this card shows
// real routines only and the footer reads "{x} enabled". Add the recommended rows + count when a source
// exists.

const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' };
const HEADER_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.32px',
  color: 'rgb(var(--ink-rgb))',
};
const LIST: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' };
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};
// `marginTop: auto` sinks the divider + footer to the card's bottom, so the footer stays aligned when
// the card is stretched to match a taller sibling in the row.
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0, marginTop: 'auto' };
const FOOTER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const SUMMARY: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const PREVIEW_COUNT = 3;

export function Home2RoutinesCard() {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useRoutines();
  const { scheduleText, titleOf } = useRoutineCopy();
  const { openModal, closeModal } = useTaskSidePanel();

  if (state.kind !== 'ready' || state.routines.length === 0) return null;

  const enabledCount = state.routines.filter((r) => routineIsOn(r)).length;
  // On routines first, then the rest — a preview leads with what's live.
  const rows = [...state.routines]
    .sort((a, b) => Number(routineIsOn(b)) - Number(routineIsOn(a)))
    .slice(0, PREVIEW_COUNT);

  const onToggle = (routine: WebRoutine, next: boolean) => {
    logPageClickBi({ page_name: 'home', button_name: 'toggle_routine', state: next ? 'on' : 'off' });
    void patchRoutine(routine, { paused: !next });
  };

  // Opening a routine shows its detail in the side panel (the web "routine page"), mirroring how the
  // Tasks card opens a task. The Switch is a sibling of this clickable name, so a toggle never navigates.
  const openRoutine = (routine: WebRoutine) => {
    logPageClickBi({ page_name: 'home', button_name: 'open_routine' });
    openModal(<RoutineDetailModal routine={routine} onClose={closeModal} />, routine.id);
  };

  return (
    <Card style={CARD}>
      <div style={HEADER}>
        <span style={HEADER_LEFT}>
          <RefreshCw size={16} aria-hidden style={{ color: 'rgb(var(--ink-rgb))', flexShrink: 0 }} />
          <span style={TITLE}>{t('routines.title')}</span>
        </span>
      </div>

      <div style={LIST}>
        {rows.map((routine) => {
          const title = titleOf(routine);
          const schedule = scheduleText(routine);
          return (
            <div key={routine.id} style={ROW}>
              <span
                className={styles.nameButton}
                style={NAME}
                role="button"
                tabIndex={0}
                onClick={() => openRoutine(routine)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openRoutine(routine);
                  }
                }}
              >
                {schedule ? `${title} · ${schedule}` : title}
              </span>
              <Switch
                checked={routineIsOn(routine)}
                onChange={(next) => onToggle(routine, next)}
                disabled={!routineToggleEnabled(routine)}
                aria-label={t('routines.toggle.label', { name: title })}
              />
            </div>
          );
        })}
      </div>

      <div style={DIVIDER} />

      <div style={FOOTER}>
        <span style={SUMMARY}>{t('home2.routines.enabled', { count: enabledCount })}</span>
        <Button
          size="medium"
          href={`${basePath}/routines`}
          onClick={() => logPageClickBi({ page_name: 'home', button_name: 'open_routines' })}
        >
          {t('home2.routines.open')}
        </Button>
      </div>
    </Card>
  );
}
